import { useState } from "react";
import usePWAInstall from "@/hooks/usePWAInstall";
import { Download, Smartphone, Share, Plus, X, Check } from "lucide-react";

/**
 * PWA Install button + modal.
 * - On Android/Desktop with beforeinstallprompt → shows a button that triggers native prompt.
 * - On iOS Safari → shows instructions modal (Add to Home Screen).
 * - Hidden if app is already installed or install is not available.
 *
 * variant: "button" (default, for TopBar) | "cta" (large, for landing hero) | "pill" (small chip)
 */
export default function InstallAppButton({ variant = "button", className = "" }) {
  const { available, installed, platform, install, hasNativePrompt } = usePWAInstall();
  const [showIosModal, setShowIosModal] = useState(false);
  const [installed2, setInstalled2] = useState(false);

  if (installed) return null;
  if (!available) return null;

  const handleClick = async () => {
    if (hasNativePrompt) {
      const r = await install();
      if (r.ok) setInstalled2(true);
    } else if (platform === "ios") {
      setShowIosModal(true);
    }
  };

  if (installed2) return null;

  const base = "font-bold transition inline-flex items-center gap-1.5";
  const styles = {
    button: `${base} px-3 py-2 rounded-xl bg-primary-brand text-white hover:opacity-95 text-sm talabya-shadow-float`,
    cta: `${base} px-6 py-4 rounded-2xl bg-white text-primary-brand border-2 border-primary-brand hover:bg-primary-light`,
    pill: `${base} px-3 py-1.5 rounded-lg bg-primary-light text-primary-brand text-xs hover:bg-primary-brand hover:text-white`,
  };

  return (
    <>
      <button onClick={handleClick} className={`${styles[variant] || styles.button} ${className}`} data-testid="install-app-btn">
        <Download size={variant === "cta" ? 20 : 14}/>
        <span>{variant === "cta" ? "حمّل التطبيق" : "تثبيت التطبيق"}</span>
      </button>

      {showIosModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={() => setShowIosModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full sm:max-w-md rounded-3xl shadow-2xl overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-[var(--border)]">
              <div className="flex items-center gap-2">
                <Smartphone className="text-primary-brand"/>
                <h2 className="text-xl font-black" style={{fontFamily:'Cairo'}}>تثبيت التطبيق</h2>
              </div>
              <button onClick={() => setShowIosModal(false)} className="w-8 h-8 rounded-full bg-[var(--surface-alt)] flex items-center justify-center"><X size={16}/></button>
            </div>
            <div className="p-5">
              <p className="text-sm text-[var(--text-secondary)] mb-4">اتبع الخطوات التالية لتثبيت طلبية على شاشتك الرئيسية:</p>
              <ol className="flex flex-col gap-3">
                {[
                  {n: 1, icon: <Share size={18}/>, text: "اضغط زر المشاركة (Share) في متصفح Safari"},
                  {n: 2, icon: <Plus size={18}/>, text: "اختر «إضافة إلى الشاشة الرئيسية»"},
                  {n: 3, icon: <Check size={18}/>, text: "اضغط «إضافة» — سيظهر تطبيق طلبية على شاشتك"},
                ].map((s) => (
                  <li key={s.n} className="flex items-start gap-3 p-3 bg-[var(--surface-alt)] rounded-2xl">
                    <div className="w-8 h-8 rounded-full bg-primary-brand text-white flex items-center justify-center font-black shrink-0" style={{fontFamily:'Cairo'}}>{s.n}</div>
                    <div className="flex-1">
                      <div className="text-primary-brand mb-1">{s.icon}</div>
                      <div className="text-sm font-bold">{s.text}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <button onClick={() => setShowIosModal(false)} className="w-full mt-4 py-3 rounded-xl bg-primary-brand text-white font-bold">
                فهمت
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
